import type { ComponentType } from 'react';
import { useDispatch } from 'react-redux';
import { Download, EyeOff, Sparkles, Trash2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { DESKTOP_BREAKPOINT_QUERY } from '@/config/ui.constants';
import type { ServiceType } from '@/components/ui/carousel';
import AssetSupportIcon from '../assets-action-bar/icons/AssetSupportIcon';
import AssetEditIcon from '../assets-action-bar/icons/AssetEditIcon';
import { clearAllSelections } from '../../slice/ImageAssetSlice';
import { usePhotosActions } from '../assets-action-bar/usePhotosActions';
import AssetServicesSheet from './AssetServicesSheet';

type AssetsSelectionBarProps = {
  services: ServiceType[];
  onPickService: (service: ServiceType) => void;
  onOpenDownload: () => void;
  onOpenSupport: () => void;
  /** Withheld by the page when staging is un-gated for this user. */
  onOpenStaging?: () => void;
  /** Withheld by the page when delete is un-gated (canDeleteAssets + photoset). */
  onOpenDelete?: () => void;
  /**
   * Services-sheet visibility, owned by the page (QA round 5 follow-up): the
   * Order Service sheet's back arrow reopens this sheet, and that lives a
   * component tree away — so the open state can't be private to this bar.
   */
  servicesSheetOpen: boolean;
  onServicesSheetOpenChange: (open: boolean) => void;
};

type SelectionAction = {
  label: string;
  Icon: ComponentType<{ className?: string }>;
  onClick: () => void;
  disabled?: boolean;
  /** Renders text-destructive at both widths (Trash). */
  destructive?: boolean;
  /** Carries an "Internal" badge on desktop; sub-lg omits it for space. */
  internalBadge?: boolean;
  /** Present below lg only — desktop reaches services via the control row's pills. */
  subLgOnly?: boolean;
};

// Floating selection action bar (2026-08-19 design update, spec §1.10):
// while any photo is checked, the selection-scoped bulk actions live here —
// they left the kebab (Hide/Stage/Delete) and the sub-lg pills row
// (Services). Sticky-bottom inside the page's scroll column on purpose: the
// app-shell <main> keeps `relative` for other absolute-rooted pages (roadmap
// Known hazard), so this bar must not anchor to it.
const AssetsSelectionBar = ({
  services,
  onPickService,
  onOpenDownload,
  onOpenSupport,
  onOpenStaging,
  onOpenDelete,
  servicesSheetOpen,
  onServicesSheetOpenChange
}: AssetsSelectionBarProps) => {
  const isDesktop = useMediaQuery(DESKTOP_BREAKPOINT_QUERY);
  const dispatch = useDispatch();
  const {
    editEnabled,
    selectedCount,
    stageableSelectedCount,
    deletableSelectedCount,
    handleHideSelected
  } = usePhotosActions();

  // deletableSelectedCount is the full checked pool (hidden included,
  // DV-2524) — the bar keys visibility to it so a hidden-only selection
  // still surfaces its one valid action (Trash).
  if (deletableSelectedCount === 0) return null;

  const countLabel = `${deletableSelectedCount} ${
    deletableSelectedCount === 1 ? 'Photo' : 'Photos'
  } Selected`;
  const clear = () => dispatch(clearAllSelections());

  // Action set and gates are identical across widths except Services (sub-lg
  // only). Declared once here rather than twice in the two JSX branches, so a
  // gate can never drift between desktop and mobile.
  const actions: SelectionAction[] = [
    {
      label: 'Download',
      Icon: Download,
      onClick: onOpenDownload,
      disabled: selectedCount === 0
    },
    ...(services.length > 0
      ? [
          {
            label: 'Services',
            // The same image-edits glyph the desktop viewer's toolbar uses
            // for this action (Steffon, QA round 5 follow-up) — AssetEditIcon
            // is its currentColor form, same precedent as AssetSupportIcon.
            Icon: AssetEditIcon,
            onClick: () => onServicesSheetOpenChange(true),
            disabled: selectedCount === 0,
            subLgOnly: true
          }
        ]
      : []),
    ...(onOpenStaging
      ? [
          {
            label: 'Stage with AI',
            Icon: Sparkles,
            onClick: onOpenStaging,
            disabled: stageableSelectedCount === 0,
            internalBadge: true
          }
        ]
      : []),
    ...(editEnabled
      ? [
          {
            label: 'Hide',
            Icon: EyeOff,
            onClick: handleHideSelected,
            disabled: selectedCount === 0
          }
        ]
      : []),
    { label: 'Support', Icon: AssetSupportIcon, onClick: onOpenSupport },
    ...(onOpenDelete
      ? [
          {
            label: 'Trash',
            Icon: Trash2,
            onClick: onOpenDelete,
            destructive: true,
            internalBadge: true
          }
        ]
      : [])
  ];

  // aria-label rather than relying on the rendered text: the label is a
  // `text-[11px]` caption sub-lg and the desktop variant appends an
  // "Internal" badge, so the accessible name is pinned here instead of
  // shifting with the breakpoint.
  const desktopActions = actions
    .filter((action) => !action.subLgOnly)
    .map(({ label, Icon, onClick, disabled, destructive, internalBadge }) => (
      <Button
        key={label}
        variant="ghost"
        type="button"
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        className={cn('shrink-0', destructive && 'text-destructive')}
      >
        <Icon className="h-4 w-4" />
        {label}
        {internalBadge && <Badge variant="secondary">Internal</Badge>}
      </Button>
    ));

  const subLgActions = actions.map(
    ({ label, Icon, onClick, disabled, destructive }) => (
      <Button
        key={label}
        variant="ghost"
        type="button"
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        className={cn(
          // h-auto + max-lg:h-auto cancels the size axis's fixed heights
          // (h-10 / max-lg:h-11) so the icon-over-label stack sets its own
          // height; min-h-11 keeps the 44px tap-target floor.
          'h-auto max-lg:h-auto min-h-11 w-16 shrink-0 flex-col gap-1 px-1 py-2',
          destructive && 'text-destructive'
        )}
      >
        <Icon className="h-5 w-5" />
        <span className="text-[11px] font-semibold leading-none">{label}</span>
      </Button>
    )
  );

  return (
    <div
      data-testid="assets-selection-bar"
      className={cn(
        // Sticky inside the page's scroll column, and the LAST child of it:
        // the bar floats over the grid while there is still column to scroll,
        // then comes to rest at the column's end. What the CSS guarantees is
        // that it never settles over a photo — at rest it sits within the
        // grid's own bottom padding (bottom-4 keeps it ~16px up), which on a
        // short, non-scrolling photoset is where it stays.
        'pointer-events-none sticky bottom-4 z-20',
        isDesktop ? 'flex justify-center px-6' : 'mx-3'
      )}
    >
      {isDesktop ? (
        // max-w-full + overflow-x-auto: the action count varies with role and
        // gating, and at exactly lg the widest set (count label + 6 labeled
        // actions + two Internal badges + Clear) can exceed the column. It
        // scrolls inside the card rather than widening the page — the
        // viewport suite's no-horizontal-overflow gate. scrollbar-none: the
        // bar scrolls without showing a scrollbar (design 2026-08-19 round 4).
        <div className="pointer-events-auto flex max-w-full items-center gap-1 overflow-x-auto scrollbar-none rounded-2xl border border-border bg-card px-3 py-2 shadow-lg">
          <span className="whitespace-nowrap px-2 text-sm font-bold text-foreground">
            {countLabel}
          </span>
          <span aria-hidden className="h-8 w-px bg-border" />
          {desktopActions}
          <span aria-hidden className="h-8 w-px bg-border" />
          <Button
            variant="ghost"
            type="button"
            aria-label="Clear"
            onClick={clear}
            className="shrink-0 text-primary"
          >
            <X className="h-4 w-4" />
            Clear
          </Button>
        </div>
      ) : (
        <div className="pointer-events-auto rounded-2xl border border-border bg-card p-2 shadow-lg">
          <div className="flex items-center justify-between gap-2 px-2 pb-1">
            <span className="truncate text-sm font-bold text-foreground">
              {countLabel}
            </span>
            <Button
              variant="ghost"
              type="button"
              size="sm"
              aria-label="Clear"
              onClick={clear}
              className="shrink-0 text-primary"
            >
              <X className="h-4 w-4" />
              Clear
            </Button>
          </div>
          {/* Horizontal scroll rather than wrap: the action count varies with
              role/gating, and a wrapped second row would push the grid's last
              photos further behind the parked bar. scrollbar-none hides the
              bar itself (design 2026-08-19 round 4). */}
          <div className="flex gap-1 overflow-x-auto scrollbar-none">
            {subLgActions}
          </div>
        </div>
      )}

      {/* Sub-lg only, mounted beside the bar: at lg+ the control row's
          AssetServicePills is the services surface, and unmounting on resize
          also guarantees the sheet can't stay open into desktop. */}
      {!isDesktop && (
        <AssetServicesSheet
          services={services}
          selectedCount={selectedCount}
          open={servicesSheetOpen}
          onOpenChange={onServicesSheetOpenChange}
          onPickService={onPickService}
        />
      )}
    </div>
  );
};

export default AssetsSelectionBar;
